import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils';
import { apiClient } from 'api/trpc/trpc-client';
import Loader from 'reusable/mobx-helpers/Loader';
import { ServiceAgreementChangeListener } from './section/services/ProposalSectionServicesStore';
import { PaymentMethodOptionValue, ServiceRateTypeEnum } from '@gap/backend/app-services/services/models/ServiceRateTypesDto';
import { listPaymentOptionsForServiceRateType } from '../proposalsUtils';
import { notNull } from '@gap/utils/utils';
import { ProposalDto } from '@gap/backend/app-services/proposal/models/ProposalDto';
import { ProposalContactDto } from '@gap/backend/app-services/proposal/models/ProposalStep1Dto';
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore';
import ProposalsViewStore from '../ProposalsViewStore';
import PrintStore from 'reusable/printing/PrintStore';
import { reaction } from 'mobx';

interface ProposalsViewProposalStoreProps {
   proposal: ProposalDto
   orgGuardStore: OrgGuardStore
   proposalsViewStore: ProposalsViewStore
   printStore: PrintStore
}

export default class ProposalsViewProposalStore {
   sp
   loader = new Loader(true)
   proposalLogoUrl?: string
   serviceIdsToIncludInPricing
   paymentPreferences
   includedOptionalServiceIds: string[] = []
   timeViewed = new Date()
   proposalDownloadUrl?: string | undefined = undefined

   constructor(sp: ProposalsViewProposalStoreProps) {
      this.sp = sp

      this.serviceIdsToIncludInPricing = this.intialiseServiceIdsIncludedInPricing()
      this.paymentPreferences = this.initialisePaymentPreferenceMap()

      reaction(() => this.displayedLogoUrl, () => {
         // Ensure the image itself is loaded (not just the URL)
         this.loader.executeLoading(() => new Promise<void>((resolve, reject) => {
            const image = new Image()
            image.addEventListener('load', () => resolve())
            image.addEventListener('error', () => reject())
            image.src = this.displayedLogoUrl!
         }))
      })
   }

   get proposal() {
      return this.sp.proposal
   }

   get isProposalAccepted() {
      return !!this.proposal.acceptedAt
   }

   get advisorOrg() {
      const currentOrg = notNull(this.sp.orgGuardStore.org)
      return currentOrg.orgType === 'advisor'
         ? currentOrg
         : currentOrg.advisorOrg
   }

   get displayedLogoUrl() {
      return this.proposalLogoUrl || this.advisorOrgLogoUrl
   }

   get advisorOrgLogoUrl() {
      const userOrg = notNull(this.sp.orgGuardStore.org)
      return (userOrg.orgType === 'client' && userOrg.advisorOrg)
         ? userOrg.advisorOrg.logoUrl
         : userOrg.logoUrl
   }

   get signees() {
      return (this.sp.proposal.step1?.contacts || []).map<Signee>((contact) => {
         const matchingContactAgreedAt = this.proposal.response?.recipientAgreements
            .find(({ advisorClientContactId }) => advisorClientContactId === contact.id)?.agreedAt

         return {
            ...contact,
            isCurrentlyAuthenticatedUser: this.sp.orgGuardStore.userData.email === contact.email,
            agreedAt: matchingContactAgreedAt && new Date(matchingContactAgreedAt) || undefined
         }
      })
   }

   get signeesSortedByAgreedAt() {
      return this.signees.sort((a, b) => (b.agreedAt?.getTime() || 0) - (a.agreedAt?.getTime() || 0))
   }

   get currentSignee() {
      return this.signees.find(({ email }) => email === this.sp.orgGuardStore.userData.email)
   }

   get applicableServiceRateTypes() {
      return (this.proposal.step3 || []).reduce<ServiceRateTypeEnum[]>((types, { serviceRateType, services }) => [
         ...types,
         ...(services.filter(({ serviceId }) => this.serviceIdsToIncludInPricing.includes(serviceId)).length ? [serviceRateType] : [])
      ], []);
   }

   get arePaymentPreferencesComplete() {
      return Object.entries(this.paymentPreferences).every(([rateType, paymentMethod]) => {
         return !this.applicableServiceRateTypes.includes(rateType as ServiceRateTypeEnum) ||
            !!paymentMethod
      })
   }

   get numberOfSignaturesRequired() {
      return this.proposal.step4?.signees === 'certain-number-of-signees'
         ? notNull(this.proposal.step4.signeesAmount)
         : this.signees.length
   }

   get numSignaturesRemaining() {
      return this.numberOfSignaturesRequired
         - this.signees.filter(({ agreedAt }) => !!agreedAt).length
   }

   get signaturesRequiredText() {
      if (this.isProposalAccepted) {
         return 'All required signatures have been received.'
      } else if (this.numberOfSignaturesRequired === 1) {
         return '1 signature is required for this proposal to be accepted.'
      } else {
         return `${this.numSignaturesRemaining} more ${this.numSignaturesRemaining === 1 ? 'signature is' : 'signatures are'} required for this proposal to be accepted.`
      }
   }

   navigateToSignedDocument = () => {
      return this.sp.proposalsViewStore.navigateToSignedDocument()
   }

   fetch = async () => {
      this.loader.executeLoading(async () => {
         const clientId = this.proposal.advisorClientId
         if (!clientId) {
            return
         }

         if (this.sp.orgGuardStore.org?.orgType === 'client' && this.proposal.acceptedAt) {
            this.proposalDownloadUrl = (await apiClient.proposals.getProposalsDownloadUrl.query({ proposalId: this.proposal.id })).url
         }

         this.proposalLogoUrl = await this.getLogoUrl({ fileName: this.proposal.step1?.logoFileName })
      })
   }

   getLogoUrl = async ({ fileName }: { fileName?: string }) => {
      if (!fileName) {
         return undefined
      }

      const { url } = await apiClient.proposals.getProposalsLogo.query({ orgId: this.sp.proposal.advisorOrgId, fileName, proposalId: this.proposal.id })

      return url
   }

   onServiceAgreementChange: ServiceAgreementChangeListener = ({ serviceAgreementMap, includedOptionalServiceIds }) => {
      this.includedOptionalServiceIds = includedOptionalServiceIds
      this.serviceIdsToIncludInPricing = Object.entries(serviceAgreementMap)
         .reduce<string[]>((agreedIds, [id, isAgreed]) => [
            ...agreedIds,
            ...(isAgreed ? [id] : []),
         ], [])

      const uninitialisedRateTypePaymentPreferences = this.applicableServiceRateTypes.filter((serviceRateType) => !this.paymentPreferences[serviceRateType])
      uninitialisedRateTypePaymentPreferences.forEach((rateType) => {
         this.paymentPreferences[rateType] = null
      })
   }

   intialiseServiceIdsIncludedInPricing = () => {
      // Initialise with recommended services excluded
      return (this.proposal.step2 || [])
         .reduce<string[]>((ids, { serviceId, servicePriceType }) => [
            ...ids,
            ...(servicePriceType === 'agreed' || this.proposal.response?.includedOptionalServiceIds.includes(serviceId) ? [serviceId] : []),
         ], []);
   }

   initialisePaymentPreferenceMap = () => {
      return this.applicableServiceRateTypes.reduce<Partial<Record<ServiceRateTypeEnum, PaymentMethodOptionValue | null>>>((values, rateType) => {
         const pricingItem = notNull((this.proposal.step3 || []).find(({ serviceRateType }) => serviceRateType === rateType))
         const paymentOptionsForCategory = listPaymentOptionsForServiceRateType({ pricingItem })

         return {
            ...values,
            [rateType]: this.proposal.response?.paymentPreferences[pricingItem.serviceRateType]
               || (paymentOptionsForCategory.length === 1
                  // If there is only one option for a given service rate type, select it
                  ? paymentOptionsForCategory[0]
                  : null),
         }
      }, {})
   }
}

export interface Signee extends ProposalContactDto {
   agreedAt?: Date,
   isCurrentlyAuthenticatedUser?: boolean,
}

export const { Provider: ProposalsViewProposalStoreProvider, useProvidedStore: useProposalsViewProposalStore } =
   createStoreContext(ProposalsViewProposalStore)
